import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/mocks/categories";

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden">
      {/* Soft decorative blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-primary-100/60 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-accent-100/50 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-14 md:px-6 lg:grid-cols-2 lg:py-20">
        {/* Left copy */}
        <div className="flex flex-col items-start">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs font-semibold text-primary-800">
            <i className="ri-sparkling-2-line" aria-hidden="true"></i>
            {t("home.hero.badge")}
          </span>

          <h1 className="mt-6 text-4xl font-bold leading-tight text-foreground-950 md:text-5xl">
            {t("home.hero.title")}
            <span className="block text-2xl font-semibold text-primary-600 md:text-3xl">
              {t("home.hero.titleAccent")}
            </span>
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
            {t("home.hero.subtitle")}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-600 cursor-pointer"
            >
              {t("common.buttons.mainCta")}
              <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
            </a>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-accent-600 cursor-pointer"
            >
              {t("common.buttons.buyNow")}
              <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
            </a>
          </div>

          <p className="mt-5 flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-shield-check-line text-primary-500" aria-hidden="true"></i>
            {t("home.hero.trust")}
          </p>
        </div>

        {/* Right: spreadsheet mockup */}
        <div className="relative">
          <div className="rounded-2xl border border-background-200 bg-white p-5 md:p-6">
            <div className="flex items-center justify-between border-b border-background-100 pb-4">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-file-list-3-line" aria-hidden="true"></i>
                </span>
                <span className="font-heading text-sm font-semibold text-foreground-900">
                  {t("home.hero.mockup.label")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-background-300"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-background-300"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-primary-400"></span>
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-background-100">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-background-100 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-3 py-2.5">{t("home.hero.mockup.header")}</th>
                    <th className="px-3 py-2.5">{t("home.hero.mockup.headerProduct")}</th>
                    <th className="hidden px-3 py-2.5 sm:table-cell">
                      {t("home.hero.mockup.headerResource")}
                    </th>
                    <th className="px-3 py-2.5">{t("home.hero.mockup.headerQc")}</th>
                    <th className="hidden px-3 py-2.5 md:table-cell">
                      {t("home.hero.mockup.headerUpdated")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {[
                    ["row1", "row1Product", "row1Qc"],
                    ["row2", "row2Product", "row2Qc"],
                    ["row3", "row3Product", "row3Qc"],
                    ["row4", "row4Product", "row4Qc"],
                  ].map(([cat, product, qc], i) => (
                    <tr key={cat} className="text-foreground-700">
                      <td className="px-3 py-3 font-medium text-foreground-900">
                        {t(`home.hero.mockup.${cat}`)}
                      </td>
                      <td className="px-3 py-3">{t(`home.hero.mockup.${product}`)}</td>
                      <td className="hidden px-3 py-3 sm:table-cell">
                        <span className="inline-flex items-center gap-1.5 text-foreground-500">
                          <i className="ri-link text-xs" aria-hidden="true"></i>
                          Link
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                            t(`home.hero.mockup.${qc}`).toLowerCase() === "yes" ||
                            t(`home.hero.mockup.${qc}`).toLowerCase() === "ja" ||
                            t(`home.hero.mockup.${qc}`).toLowerCase() === "oui"
                              ? "bg-primary-100 text-primary-700"
                              : "bg-accent-100 text-accent-700"
                          }`}
                        >
                          {t(`home.hero.mockup.${qc}`)}
                        </span>
                      </td>
                      <td className="hidden px-3 py-3 text-foreground-400 md:table-cell">
                        {`2026-0${i + 1}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between rounded-xl bg-background-50 px-4 py-3">
              <span className="text-xs font-medium text-foreground-500">
                {t("home.categories.allExternal")}
              </span>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                {t("common.buttons.explore")}
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}